/**
 * GameLinesModule — MLB + NFL game lines: the raw price table, and the SAME
 * odds history that powers the PNG charts posted to Discord, as interactive
 * Chart.js line charts.
 *
 * WHAT THIS SHOWS, AND WHY IT IS NOT A SNAPSHOT
 * The hourly odds monitors append every book's price to `odds_history` in
 * odds_v3.db on every pull. That accumulated series is what
 * generate_moneyline_charts() plots — price MOVEMENT over time, one line per
 * bookmaker, gold dashed consensus, inverted American-odds axis, away panel
 * above home panel.
 *
 * A first cut of this module charted only the current snapshot — every book's
 * price at one instant. That is NOT the same data and it is not what the
 * graphics show. It was wrong and is replaced here.
 *
 * DATA
 *   history  GET {MLB_PROXY}/odds/history?game_id=&market=&since=
 *            Read-only per-book American-odds time series straight out of
 *            odds_history. Serves `series[]` (real books) and `consensus[]`
 *            (the synthetic `_average` book) separately.
 *   current  GET {ODDS_PROXY}/odds/:sport
 *            The TheOddsAPI wrapper — 9-key rotation, CORS, 60s cache. Used
 *            for the game list and the current-price table.
 *
 * If the history endpoint is not reachable the chart says so plainly and the
 * table still works. It never draws a substitute chart from other data.
 *
 * Refresh 60s (matches the wrapper cache). CSS prefix: glm-
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
  Chart, LineController, LineElement, PointElement,
  LinearScale, TimeScale, CategoryScale, Tooltip, Legend, Filler,
} from 'chart.js'
import { fetchFromOddsProxy, fetchFromProxy } from '../lib/api'
import { getTeamLogoPath, getLeagueLogoPath } from '../lib/teamLogos'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import { Table2, Activity, RefreshCw, AlertCircle } from 'lucide-react'
import './GameLinesModule.css'

Chart.register(LineController, LineElement, PointElement, LinearScale,
  TimeScale, CategoryScale, Tooltip, Legend, Filler)

/** Straight from mlb_unified_odds_monitor.BOOKMAKER_COLORS, so a book is the
 *  same colour here as in the PNG posted to Discord. */
const BOOK_COLORS = {
  draftkings: '#53D337', fanduel: '#1A73E8', betmgm: '#FDB913',
  betrivers: '#003DA5', caesars: '#522398', pointsbetau: '#00B5E2',
  bovada: '#CC0000', betonlineag: '#FF4500', mybookieag: '#800080',
  betus: '#006400', lowvig: '#4B0082',
}
const CONSENSUS_GOLD = '#FFD700'

const SPORTS = [
  { id: 'baseball_mlb', label: 'MLB' },
  { id: 'americanfootball_nfl', label: 'NFL' },
  { id: 'americanfootball_nfl_preseason', label: 'NFL Preseason' },
]
const MARKETS = [
  { id: 'h2h', label: 'Moneyline' },
  { id: 'spreads', label: 'Spread' },
  { id: 'totals', label: 'Total' },
]
/* AUTO-REFRESH IS OFF BY DEFAULT, AND THIS IS NOT A STYLE CHOICE.
 *
 * Every /odds/:sport call costs 3 TheOddsAPI credits against a pool shared with
 * the MLB and NFL monitors. This module originally polled every 60s. The
 * wrapper's 60s cache means many VIEWERS share one upstream call, but a single
 * tab polling on a timer still forces one call per minute all by itself:
 *
 *     60s polling  ->  60 calls/hr  ->  180 credits/hr  ->  4,320 credits/day
 *
 * The whole 9-key pool is 4,500/month. One tab left open drained it inside a
 * day on 2026-08-25 and took the hourly monitors down with it — the wrapper
 * started returning 401 "Usage quota has been reached" for every sport.
 *
 * So: fetch once on mount, and refresh only when a human asks. If a timer is
 * ever reintroduced, MANUAL_ONLY must stay false-by-default and the interval
 * must never go below REFRESH_FLOOR_MS.
 */
const MANUAL_ONLY = true
const REFRESH_FLOOR_MS = 900000        // 15 min = 12 credits/hr, if ever enabled
const CREDITS_PER_FETCH = 3

const bookColor = (k) => BOOK_COLORS[k] || '#8a9aaf'
const fmtAmerican = (a) =>
  a === null || a === undefined || !Number.isFinite(a) ? '—' : a > 0 ? `+${a}` : `${a}`

/** American -> decimal. We PLOT decimal and LABEL American: American is
 *  discontinuous across zero (-100 and +100 are neighbours in value but 200
 *  apart numerically), so plotting it directly makes a line chart misstate the
 *  size of a move. Decimal is monotonic, so the geometry is honest. */
function toDecimal(american) {
  const a = Number(american)
  if (!Number.isFinite(a) || a === 0) return null
  return a > 0 ? a / 100 + 1 : 100 / Math.abs(a) + 1
}
function toAmerican(dec) {
  if (!Number.isFinite(dec) || dec <= 1) return null
  return dec >= 2 ? Math.round((dec - 1) * 100) : -Math.round(100 / (dec - 1))
}
const impliedPct = (dec) => (dec ? (100 / dec).toFixed(1) + '%' : '—')
const hhmm = (iso) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function TeamLogo({ team, sport, size = 18 }) {
  const src = getTeamLogoPath?.(team, sport)
  const [bad, setBad] = useState(false)
  if (!src || bad) return null
  return <img className="glm-logo" src={src} alt="" width={size} height={size}
    onError={() => setBad(true)} loading="eager" />
}

function BookLogo({ bookKey, size = 16 }) {
  const id = resolveBookIdentity?.(bookKey)
  const [bad, setBad] = useState(false)
  if (!id?.logoUrl || bad) return null
  return <img className="glm-booklogo" src={id.logoUrl} alt="" width={size}
    height={size} onError={() => setBad(true)} />
}

/** One Chart.js line chart for a single outcome: every book over time, plus the
 *  gold dashed consensus. This is the per-panel unit the PNG uses. */
function OutcomeChart({ outcome, series, consensus, market }) {
  const canvasRef = useRef(null)
  const chartRef = useRef(null)

  useEffect(() => {
    if (!canvasRef.current) return
    const mine = series.filter((s) => s.outcome === outcome)
    if (!mine.length) return

    // A shared, sorted time axis across every book keeps the lines comparable.
    const stamps = [...new Set(mine.flatMap((s) => s.points.map((p) => p.t)))].sort()
    const labels = stamps.map(hhmm)

    const datasets = mine.map((s) => {
      const byT = new Map(s.points.map((p) => [p.t, p]))
      return {
        label: s.bookmaker_title || s.bookmaker,
        _book: s.bookmaker,
        data: stamps.map((t) => toDecimal(byT.get(t)?.price) ?? null),
        _pts: stamps.map((t) => byT.get(t) ?? null),
        borderColor: bookColor(s.bookmaker),
        backgroundColor: bookColor(s.bookmaker),
        borderWidth: 1.8,
        pointRadius: 2,
        pointHoverRadius: 5,
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1,
        spanGaps: true,
        tension: 0,
      }
    })

    const cons = consensus.find((c) => c.outcome === outcome)
    if (cons) {
      const byT = new Map(cons.points.map((p) => [p.t, p]))
      datasets.push({
        label: 'AVERAGE',
        _book: '_average',
        data: stamps.map((t) => toDecimal(byT.get(t)?.price) ?? null),
        _pts: stamps.map((t) => byT.get(t) ?? null),
        borderColor: CONSENSUS_GOLD,
        backgroundColor: CONSENSUS_GOLD,
        borderDash: [6, 4],
        borderWidth: 2.4,
        pointRadius: 0,
        pointHoverRadius: 4,
        spanGaps: true,
        tension: 0,
      })
    }

    Chart.getChart(canvasRef.current)?.destroy()
    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        scales: {
          x: {
            ticks: { color: '#8a9aaf', font: { size: 9 }, maxRotation: 0,
                     autoSkipPadding: 18 },
            grid: { color: 'rgba(255,255,255,.04)' },
          },
          y: {
            // Inverted so a SHORTER price sits HIGHER, matching the PNG.
            reverse: true,
            ticks: {
              color: '#b0c0d6', font: { size: 9 },
              callback: (v) => fmtAmerican(toAmerican(v)),
            },
            grid: { color: 'rgba(255,255,255,.06)' },
          },
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: '#c3cddb', boxWidth: 10, boxHeight: 2, font: { size: 10 },
              usePointStyle: false,
              generateLabels: (c) => Chart.defaults.plugins.legend.labels
                .generateLabels(c).map((l) => ({
                  ...l,
                  fontColor: l.text === 'AVERAGE' ? CONSENSUS_GOLD : '#c3cddb',
                })),
            },
          },
          tooltip: {
            callbacks: {
              title: (items) => items[0]?.label ?? '',
              label: (ctx) => {
                const p = ctx.dataset._pts?.[ctx.dataIndex]
                const am = p?.price ?? toAmerican(ctx.parsed.y)
                const line = market !== 'h2h' && p?.point != null
                  ? ` @ ${p.point > 0 ? '+' : ''}${p.point}` : ''
                return `${ctx.dataset.label}: ${fmtAmerican(am)}${line}`
                  + `  (${impliedPct(ctx.parsed.y)})`
              },
            },
          },
        },
      },
    })
    return () => {
      const c = canvasRef.current ? Chart.getChart(canvasRef.current) : null
      ;(c || chartRef.current)?.destroy()
      chartRef.current = null
    }
  }, [outcome, series, consensus, market])

  return (
    <figure className="glm-panel">
      <figcaption className="glm-panel-cap">{outcome}</figcaption>
      <div className="glm-panel-canvas"><canvas ref={canvasRef} /></div>
    </figure>
  )
}

export default function GameLinesModule() {
  const [sport, setSport] = useState(SPORTS[0].id)
  const [market, setMarket] = useState('h2h')
  const [view, setView] = useState('chart')
  const [games, setGames] = useState([])
  const [gameId, setGameId] = useState(null)
  const [status, setStatus] = useState('loading')
  const [message, setMessage] = useState(null)
  const [quota, setQuota] = useState({ remaining: null })
  const [updated, setUpdated] = useState(null)
  const [hist, setHist] = useState(null)
  const [histState, setHistState] = useState('idle')
  const [histMsg, setHistMsg] = useState(null)
  // Sortable price table. This existed, and a rewrite silently dropped it —
  // restored, with direction toggling so a second click reverses the column.
  const [sort, setSort] = useState({ key: 'book', dir: 1 })
  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))

  const loadGames = useCallback(async () => {
    const res = await fetchFromOddsProxy(`/odds/${sport}`, {
      regions: 'us', markets: 'h2h,spreads,totals', oddsFormat: 'american',
    })
    if (res.status !== 'ok' || !Array.isArray(res.data)) {
      // CLEAR THE STALE LIST. Leaving the previous sport's games on screen after
      // a failed fetch is why switching to NFL appeared to do nothing at all:
      // the request 502'd on an exhausted quota and the MLB games simply stayed
      // rendered, with no indication anything had gone wrong.
      setGames([]); setGameId(null); setHist(null); setHistState('idle')
      setStatus(res.status || 'error')
      setMessage(res.message || 'Could not reach the odds proxy')
      if (res.remaining != null) setQuota({ remaining: res.remaining })
      return
    }
    setGames(res.data)
    setQuota({ remaining: res.remaining })
    setGameId((p) => (p && res.data.some((g) => g.id === p) ? p : res.data[0]?.id ?? null))
    setStatus('ok'); setMessage(null); setUpdated(new Date())
  }, [sport])

  useEffect(() => {
    // Sport changed: drop everything from the previous sport BEFORE fetching, so
    // a slow or failing request can never leave the old sport's board on screen.
    setGames([]); setGameId(null); setHist(null); setHistState('idle')
    setStatus('loading')
    loadGames()
    if (MANUAL_ONLY) return                    // see the note above REFRESH_FLOOR_MS
    const t = setInterval(() => {
      if (!document.hidden) loadGames()        // never bill for a hidden tab
    }, REFRESH_FLOOR_MS)
    return () => clearInterval(t)
  }, [loadGames])

  // ---- odds history: the series that powers the Discord charts -------------
  useEffect(() => {
    if (!gameId) return
    let cancelled = false
    setHistState('loading'); setHistMsg(null)
    ;(async () => {
      const res = await fetchFromProxy('/odds/history', { game_id: gameId, market })
      if (cancelled) return
      if (res.status === 'ok' && res.data && Array.isArray(res.data.series)) {
        setHist(res.data)
        setHistState(res.data.series.length ? 'ok' : 'empty')
      } else {
        setHist(null); setHistState('error')
        setHistMsg(res.message || 'odds history is not reachable')
      }
    })()
    return () => { cancelled = true }
  }, [gameId, market])

  const game = useMemo(() => games.find((g) => g.id === gameId) || null, [games, gameId])

  const rows = useMemo(() => {
    if (!game) return []
    const out = []
    for (const bm of game.bookmakers || []) {
      const mkt = (bm.markets || []).find((m) => m.key === market)
      if (!mkt) continue
      for (const o of mkt.outcomes || []) {
        out.push({
          book: bm.key, bookTitle: bm.title || bm.key,
          lastUpdate: mkt.last_update || bm.last_update || null,
          outcome: o.name, point: o.point ?? null,
          american: Number(o.price), decimal: toDecimal(o.price),
        })
      }
    }
    return out
  }, [game, market])

  const consensusNow = useMemo(() => {
    const acc = {}
    for (const r of rows) if (r.decimal) (acc[r.outcome] ||= []).push(r.decimal)
    const o = {}
    for (const [k, v] of Object.entries(acc)) {
      const mean = v.reduce((a, b) => a + b, 0) / v.length
      o[k] = { decimal: mean, american: toAmerican(mean), n: v.length }
    }
    return o
  }, [rows])

  /** Sorted view of the price rows. Book/outcome sort as text; price, line,
   *  implied and vs-average sort numerically off the DECIMAL value, so the
   *  ordering is correct across the American sign boundary. */
  const sortedRows = useMemo(() => {
    const c = [...rows]
    const { key, dir } = sort
    c.sort((a, b) => {
      let r = 0
      if (key === 'price' || key === 'implied') r = (a.decimal ?? 0) - (b.decimal ?? 0)
      else if (key === 'line') r = (a.point ?? 0) - (b.point ?? 0)
      else if (key === 'delta') {
        const ca = consensusNow[a.outcome], cb = consensusNow[b.outcome]
        r = ((ca?.american != null ? a.american - ca.american : 0)
           - (cb?.american != null ? b.american - cb.american : 0))
      } else if (key === 'outcome') r = String(a.outcome).localeCompare(String(b.outcome))
      else if (key === 'updated') r = String(a.lastUpdate || '').localeCompare(String(b.lastUpdate || ''))
      else r = String(a.bookTitle).localeCompare(String(b.bookTitle))
      return r * dir
    })
    return c
  }, [rows, sort, consensusNow])

  const outcomes = useMemo(
    () => (hist?.series?.length
      ? [...new Set(hist.series.map((s) => s.outcome))]
      : [...new Set(rows.map((r) => r.outcome))]), [hist, rows])

  const leagueLogo = getLeagueLogoPath?.(sport)

  return (
    <div className="glm-root">
      <header className="glm-head">
        <div className="glm-title">
          {leagueLogo
            ? <img className="glm-league" src={leagueLogo} alt="" height={20} />
            : <Activity size={16} />}
          <span>TheOddsAPI — Game Lines</span>
        </div>
        <div className="glm-meta">
          {quota.remaining != null && (
            <span className="glm-quota" title="Credits left on the key the proxy used">
              {quota.remaining} credits
            </span>
          )}
          {updated && <span>{updated.toLocaleTimeString()}</span>}
          <button className="glm-refresh" onClick={loadGames}
            aria-label={`Refresh — costs ${CREDITS_PER_FETCH} TheOddsAPI credits`}
            title={`Refresh. Costs ${CREDITS_PER_FETCH} credits from the shared pool, unless the wrapper's 60s cache is still warm. There is no auto-refresh — a 60s timer drained the whole month's pool in a day.`}>
            <RefreshCw size={13} />
          </button>
        </div>
      </header>

      <nav className="glm-bar">
        <div className="glm-seg" role="tablist" aria-label="Sport">
          {SPORTS.map((s) => (
            <button key={s.id} role="tab" aria-selected={sport === s.id}
              className={`glm-seg-btn ${sport === s.id ? 'is-on' : ''}`}
              onClick={() => setSport(s.id)}>{s.label}</button>
          ))}
        </div>
        <div className="glm-seg" role="tablist" aria-label="Market">
          {MARKETS.map((m) => (
            <button key={m.id} role="tab" aria-selected={market === m.id}
              className={`glm-seg-btn ${market === m.id ? 'is-on' : ''}`}
              onClick={() => setMarket(m.id)}>{m.label}</button>
          ))}
        </div>
        <div className="glm-seg glm-seg-view" role="tablist" aria-label="View">
          <button role="tab" aria-selected={view === 'chart'}
            title="How each book's price has MOVED over time — one line per bookmaker across every hourly pull. Same series the Discord chart is drawn from."
            className={`glm-seg-btn ${view === 'chart' ? 'is-on' : ''}`}
            onClick={() => setView('chart')}>
            <Activity size={12} /> Movement
          </button>
          <button role="tab" aria-selected={view === 'table'}
            title="What every book is offering RIGHT NOW — one row per book, with the consensus and each book's distance from it."
            className={`glm-seg-btn ${view === 'table' ? 'is-on' : ''}`}
            onClick={() => setView('table')}>
            <Table2 size={12} /> Prices
          </button>
        </div>
      </nav>

      {games.length > 0 && (
        <div className="glm-gamerow">
          {game && (
            <span className="glm-matchup">
              <TeamLogo team={game.away_team} sport={sport} />
              <span>{game.away_team}</span>
              <span className="glm-at">@</span>
              <TeamLogo team={game.home_team} sport={sport} />
              <span>{game.home_team}</span>
            </span>
          )}
          <select className="glm-game" value={gameId ?? ''}
            onChange={(e) => setGameId(e.target.value)} aria-label="Game">
            {games.map((g) => (
              <option key={g.id} value={g.id}>
                {g.away_team} @ {g.home_team}
              </option>
            ))}
          </select>
        </div>
      )}

      {status === 'loading' && <div className="glm-note">Loading…</div>}

      {status !== 'ok' && status !== 'loading' && (
        <div className="glm-note glm-err">
          <AlertCircle size={14} /><span>{message}</span>
        </div>
      )}

      {/* ---- MOVEMENT: the same series that draws the Discord PNG ---------- */}
      {status === 'ok' && view === 'chart' && (
        <>
          {histState === 'loading' && <div className="glm-note">Loading odds history…</div>}

          {histState === 'error' && (
            <div className="glm-note glm-err">
              <AlertCircle size={14} />
              <span>
                <strong>Odds history is not reachable.</strong> {histMsg}. This
                chart plots the stored per-book series from <code>odds_history</code>
                — the same rows the hourly monitor draws its Discord charts from.
                Until <code>/odds/history</code> is deployed and routed on the
                proxy there is nothing truthful to plot, so nothing is drawn.
                The <strong>Prices</strong> tab still works.
              </span>
            </div>
          )}

          {histState === 'empty' && (
            <div className="glm-note">
              <AlertCircle size={14} />
              <span>No stored history for this game and market yet — it fills in
                one point per hourly pull.</span>
            </div>
          )}

          {histState === 'ok' && (
            <>
              <div className="glm-panels">
                {outcomes.map((oc) => (
                  <OutcomeChart key={oc} outcome={oc} market={market}
                    series={hist.series} consensus={hist.consensus || []} />
                ))}
              </div>
              <p className="glm-caption">
                {hist.point_count} stored prices across {hist.bookmaker_count} books
                since {hhmm(hist.since)}. Axis inverted so shorter prices sit higher;
                ticks labelled in American odds, plotted in decimal so the size of a
                move is drawn truthfully. Gold dashed line is the {`${'AVERAGE'}`} book.
              </p>
            </>
          )}
        </>
      )}

      {/* ---- PRICES: current snapshot, every book ------------------------- */}
      {status === 'ok' && view === 'table' && (
        rows.length === 0 ? (
          <div className="glm-note">
            <AlertCircle size={14} />
            <span>No {MARKETS.find((m) => m.id === market)?.label} prices posted for
              this game yet. Books publish markets at different times.</span>
          </div>
        ) : (
          <div className="glm-tablewrap">
            <table className="glm-table">
              <thead>
                <tr>
                  {[
                    ['book', 'Book', false, null],
                    ['outcome', 'Outcome', false, null],
                    ...(market !== 'h2h' ? [['line', 'Line', true, null]] : []),
                    ['price', 'Price', true, null],
                    ['implied', 'Implied', true, null],
                    ['delta', 'vs average', true,
                     "How far this book's price sits from the average of all books"],
                    ['updated', 'Updated', false, null],
                  ].map(([key, label, num, tip]) => (
                    <th key={key} className={num ? 'glm-num' : undefined}
                        aria-sort={sort.key === key
                          ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                      <button onClick={() => toggleSort(key)} title={tip || `Sort by ${label}`}>
                        {label}
                        <span className="glm-sortcue">
                          {sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => {
                  const c = consensusNow[r.outcome]
                  const d = c && c.american != null ? r.american - c.american : null
                  return (
                    <tr key={i}>
                      <td className="glm-bookcell">
                        <BookLogo bookKey={r.book} />
                        <span className="glm-dot" style={{ background: bookColor(r.book) }} />
                        {r.bookTitle}
                      </td>
                      <td>{r.outcome}</td>
                      {market !== 'h2h' && <td className="glm-num">{r.point ?? '—'}</td>}
                      <td className="glm-num glm-price">{fmtAmerican(r.american)}</td>
                      <td className="glm-num">{impliedPct(r.decimal)}</td>
                      <td className={`glm-num ${d > 0 ? 'glm-up' : d < 0 ? 'glm-dn' : ''}`}>
                        {d === null ? '—' : `${d > 0 ? '+' : ''}${d}`}
                      </td>
                      <td className="glm-ts">
                        {r.lastUpdate ? hhmm(r.lastUpdate) : '—'}
                      </td>
                    </tr>
                  )
                })}
                {Object.entries(consensusNow).map(([oc, c]) => (
                  <tr key={`c-${oc}`} className="glm-consensus">
                    <td className="glm-bookcell">
                      <span className="glm-dot" style={{ background: CONSENSUS_GOLD }} />
                      AVERAGE
                    </td>
                    <td>{oc}</td>
                    {market !== 'h2h' && <td className="glm-num">—</td>}
                    <td className="glm-num glm-price">{fmtAmerican(c.american)}</td>
                    <td className="glm-num">{impliedPct(c.decimal)}</td>
                    <td className="glm-num">—</td>
                    <td className="glm-ts">{c.n} books</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  )
}
